import React from 'react';
import { useInView } from '@/hooks/useInView';

interface FeatureBlockProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  imagePosition: 'left' | 'right';
}

const FeatureBlock: React.FC<FeatureBlockProps> = ({
  title,
  description,
  imageSrc,
  imageAlt,
  imagePosition
}) => {
  const { ref, inView: isInView } = useInView({ threshold: 0.2, triggerOnce: true });

  const imageClasses = `
    w-full h-auto rounded-lg shadow-md
    transition-all duration-700 ease-out
    ${isInView ? 'opacity-100 translate-x-0' : `opacity-0 ${imagePosition === 'left' ? '-translate-x-8' : 'translate-x-8'}`}
  `;

  const textClasses = `
    transition-all duration-700 ease-out delay-100
    ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}
  `;

  return (
    <div
      ref={ref as React.RefObject<HTMLDivElement>}
      className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center"
    >
      {imagePosition === 'left' ? (
        <>
          <div className={imageClasses}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />
          </div>
          <div className={textClasses}>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              {title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              {description}
            </p>
          </div>
        </>
      ) : (
        <>
          <div className={`${textClasses} order-2 lg:order-1`}>
            <h3 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
              {title}
            </h3>
            <p className="text-lg text-gray-700 leading-relaxed">
              {description}
            </p>
          </div>
          <div className={`${imageClasses} order-1 lg:order-2`}>
            <img
              src={imageSrc}
              alt={imageAlt}
              className="w-full h-auto rounded-lg"
              loading="lazy"
            />
          </div>
        </>
      )}
    </div>
  );
};

export const SolutionInAction: React.FC = () => {
  const features = [
    {
      title: "Votre DPE est-il vraiment fiable ?",
      description: "Un DPE mal renseigné peut vous coûter des milliers d'euros de travaux inutiles — ou vous faire rater une lettre atteignable sans gros chantier. DPEWizard analyse vos données ADEME avec 28 règles métier et vous donne un score de confiance avec les incohérences à corriger, noir sur blanc.",
      imageSrc: "/assets/Score_de_confiance.png",
      imageAlt: "Score de confiance à 85% avec tableau d'analyse des incohérences DPE",
      imagePosition: 'right' as const
    },
    {
      title: "Seulement les travaux pertinents pour votre bien",
      description: "Trois stratégies possibles. Vous choisissez votre objectif, DPEWizard génère le scénario adapté en un clic.",
      imageSrc: "/assets/DPEWizard_3_Scenarios.png",
      imageAlt: "Cartes des travaux conseillés avec badges de priorité et économies kWh",
      imagePosition: 'left' as const
    },
    {
      title: "Voyez vos deux lettres DPE évoluer en temps réel",
      description: "Consommation énergétique ET émissions GES — simultanément. Parce qu'améliorer l'une sans surveiller l'autre peut vous laisser bloqué en F ou G sur le critère qui détermine la mise en location de votre bien.",
      imageSrc: "/assets/Scenario_de_travaux.png",
      imageAlt: "Simulation DPE montrant l'évolution E→A (énergie) et E→C (GES) avec métriques",
      imagePosition: 'right' as const
    },
    {
      title: "Le ROI de chaque travaux, avant d'appeler un artisan",
      description: "Pour chaque poste : kWh économisés, euros économisés par an, coût estimé, retour sur investissement en années. Un tableau de synthèse consolide l'ensemble. Vous savez exactement ce que vaut chaque euro investi — avant de signer quoi que ce soit.",
      imageSrc: "/assets/Synthese_financiere.png",
      imageAlt: "Tableau de synthèse financière des travaux avec ROI color-codé",
      imagePosition: 'left' as const
    }
  ];

  return (
    <section className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
            La solution DPEWizard en action
          </h2>
        </div>

        <div className="space-y-20 lg:space-y-32">
          {features.map((feature, index) => (
            <div
              key={index}
              className={index % 2 === 1 ? 'bg-gray-50 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 py-16' : ''}
            >
              <div className={index % 2 === 1 ? 'max-w-7xl mx-auto' : ''}>
                <FeatureBlock {...feature} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default SolutionInAction;